import React, { useState } from "react";
import { motion } from "framer-motion";
import { Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { agePhoto, fileUrl } from "../lib/api";
import { YEAR_OPTIONS } from "../lib/futureSelf";

const RING = ["#00F0FF", "#B026FF", "#FFD700", "#FFB347"];

export const AgeTimeline = ({ fs, journeyId }) => {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);

  const reveal = async () => {
    setOpen(true);
    for (const y of YEAR_OPTIONS) {
      if (fs.journey?.aged_photos?.[y]) continue;
      setPending(y);
      try {
        await agePhoto(journeyId, y);
        await fs.refresh();
      } catch {
        toast.error(`Couldn't age the +${y} photo.`);
      }
    }
    setPending(null);
  };

  const frames = [
    { key: "today", label: "Today", src: fs.photoUrl },
    ...YEAR_OPTIONS.map((y) => ({ key: y, label: fs.futureAgeLabel(y), src: fileUrl(fs.journey?.aged_photos?.[y]) })),
  ];

  return (
    <>
      <button onClick={reveal} className="btn-pill text-xs mx-auto mt-3 flex" data-testid="age-timeline-btn">
        <Clock size={12} /> Reveal my age timeline
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass-strong border-white/10 max-w-3xl text-white" data-testid="age-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-light text-2xl">Watch yourself grow older</DialogTitle>
            <DialogDescription className="text-white/50 text-sm">Your face across the decades ahead.</DialogDescription>
          </DialogHeader>
          <div className="relative py-8 px-3 overflow-x-auto">
            <motion.div
              className="absolute left-[10%] top-[calc(50%-14px)] h-[2px]"
              style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF,#FFD700)", boxShadow: "0 0 12px #B026FF" }}
              initial={{ width: 0 }}
              animate={{ width: "80%" }}
              transition={{ duration: 2.4, ease: "easeInOut" }}
            />
            <div className="relative flex justify-between gap-4 min-w-[420px]">
              {frames.map((f, i) => (
                <motion.div
                  key={f.key}
                  initial={{ opacity: 0, scale: 0.6, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: i * 0.6, duration: 0.7, ease: "easeOut" }}
                  className="flex flex-col items-center gap-3"
                  data-testid={`timeline-frame-${f.key}`}
                >
                  <div
                    className="w-20 h-20 md:w-28 md:h-28 rounded-full overflow-hidden bg-[#0A051A] flex items-center justify-center"
                    style={{ border: `2px solid ${RING[i]}`, boxShadow: `0 0 26px ${RING[i]}88` }}
                  >
                    {f.src ? (
                      <motion.img key={f.src} src={f.src} alt={f.label} className="w-full h-full object-cover" initial={{ opacity: 0, filter: "blur(8px)" }} animate={{ opacity: 1, filter: "blur(0px)" }} transition={{ duration: 1 }} />
                    ) : (
                      <Loader2 className={`text-white/40 ${pending === f.key ? "animate-spin" : ""}`} />
                    )}
                  </div>
                  <div className="font-mono-label" style={{ color: RING[i] }}>{f.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
          <p className="text-xs text-white/40 text-center">
            {pending ? `Aging your photo to ${fs.futureAgeLabel(pending)}…` : "Imagined portraits, not predictions."}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};
